import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Account,
  useSideOverContext,
  useCloseCashAccount,
  useCloseBusiness,
  useCloseInvestment,
  useCloseRealEstateProperty,
  useCloseLoan,
  useClosePersonalPropertyItem,
  useCloseNoteReceivable,
  ttDetailsKeys,
  displayAccountType,
  ElementsButton,
} from '../..';
import { useQueryClient } from '@tanstack/react-query';

interface CloseAccountModalProps {
  show: boolean;
  account: Account;
  householdID: string;
  onClose: () => void;
}

export function CloseAccountModal({ onClose, show, account, householdID }: CloseAccountModalProps) {
  const queryClient = useQueryClient();
  const { t: tLoan } = useTranslation('loan');
  const { closeSideOver } = useSideOverContext();
  const { t: tCommon } = useTranslation('common');
  const { t: tInvestment } = useTranslation('investment');
  const { t: tRealEstate } = useTranslation('realEstate');
  const { t: tCashAccount } = useTranslation('cashAccount');
  const { t: taccountSideover } = useTranslation('accountSideover');

  const { closeCashAccount } = useCloseCashAccount();
  const { closeBusiness } = useCloseBusiness();
  const { closeInvestment } = useCloseInvestment();
  const { closeRealEstateProperty } = useCloseRealEstateProperty();
  const { closeLoan } = useCloseLoan();
  const { closePersonalPropertyItem } = useClosePersonalPropertyItem();
  const { closeNoteReceivable } = useCloseNoteReceivable();

  function closeAccount() {
    const onSuccess = async () => {
      await queryClient.invalidateQueries(ttDetailsKeys.base);
      onClose();
      closeSideOver();
    };

    switch (account.__typename) {
      case 'CashAccount':
        return closeCashAccount.mutate({ householdID, id: account.id }, { onSuccess });
      case 'Investment':
        return closeInvestment.mutate({ householdID, id: account.id }, { onSuccess });
      case 'Business':
        return closeBusiness.mutate({ householdID, id: account.id }, { onSuccess });
      case 'RealEstate':
        return closeRealEstateProperty.mutate({ householdID, id: account.id }, { onSuccess });
      case 'Loan':
        return closeLoan.mutate({ householdID, id: account.id }, { onSuccess });
      case 'PersonalPropertyItem':
        return closePersonalPropertyItem.mutate({ householdID, id: account.id }, { onSuccess });
      case 'NoteReceivable':
        return closeNoteReceivable.mutate({ householdID, id: account.id }, { onSuccess });
    }
  }

  const isClosing =
    closeCashAccount.isLoading ||
    closeInvestment.isLoading ||
    closeBusiness.isLoading ||
    closeLoan.isLoading ||
    closeRealEstateProperty.isLoading ||
    closePersonalPropertyItem.isLoading ||
    closeNoteReceivable.isLoading;

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" onClose={onClose} className="fixed inset-0 right-0 z-50 overflow-y-auto">
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            {account ? (
              <div
                className="absolute bottom-0 left-1/2 my-8 transform -translate-x-1/2 w-[calc(100%-2rem)] max-w-[616px] h-auto overflow-hidden rounded-2xl bg-darkMode-gray-floatingMenu p-6 text-left align-middle font-bold shadow-xl transition-all
                       md:translate-x-0 md:left-auto md:right-0 md:w-[616px] md:h-[255px] md:mx-[32px] md:p-8"
              >
                <Dialog.Title as="h3" className="text-xl md:text-2xl text-white">
                  {taccountSideover('close-account-heading', { name: account.name })}
                </Dialog.Title>
                <p className="mt-3 text-lg leading-snug text-darkMode-gray-light font-normal">
                  {taccountSideover('close-account-body', {
                    accountType: displayAccountType(
                      account,
                      tLoan,
                      tCashAccount,
                      tCommon,
                      tInvestment,
                      tRealEstate
                    ).toLowerCase(),
                  })}
                </p>
                <div className="mt-6 md:mt-8 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex h-10 items-center rounded-full border border-palette-600 px-5 text-[15px] font-bold leading-normal text-palette-600"
                    onClick={onClose}
                  >
                    {tCommon('cancel')}
                  </button>
                  <ElementsButton
                    theme="primary"
                    className="ml-4"
                    onClick={closeAccount}
                    isLoading={isClosing}
                    label={taccountSideover('close-account-button', {
                      text: displayAccountType(account, tLoan, tCashAccount, tCommon, tInvestment, tRealEstate),
                    })}
                  />
                </div>
              </div>
            ) : null}
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
